'use client';

import Pusher from 'pusher-js';
import {
  type PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../auth/use-auth';
import { useLanguage } from '../i18n/use-language';
import { ChannelsProvider } from './channel-provider';
import { PusherContext } from './pusher-provider';
import { ConnectionState } from './socket-types';
import { Client, TokenProvider } from '@pusher/push-notifications-web';

export type SocketProviderConfig = {
  clientKey: string;
  authEndpoint: string;
  instanceKey: string;
  beamsAuthEndpoint: string;
  pusherHost: string;
  pusherPort: number;
  pusherSSL: boolean;
  withBeam?: boolean;
};

export const SocketProvider = ({
  children,
  clientKey,
  withBeam,
  instanceKey,
  beamsAuthEndpoint,
  ...props
}: PropsWithChildren<SocketProviderConfig>) => {
  const { accessToken, currentUser } = useAuth();
  const { language } = useLanguage();

  useEffect(() => {
    if (!clientKey) console.error('A client key is required for pusher');
  }, [clientKey]);

  const [state, setState] = useState<ConnectionState>(ConnectionState.INIT);
  const clientRef = useRef<Pusher>(null);

  async function initBeams(
    endpoint: string,
    userId: string,
    instanceKey: string,
    accessKey: string
  ) {
    try {
      const beamsClient = new Client({
        instanceId: instanceKey,
      });

      await beamsClient.start();

      const provider = new TokenProvider({
        url: endpoint,
        headers: {
          Authorization: `Bearer ${accessKey}`,
        },
      });

      await beamsClient.setUserId(userId, provider);
      await beamsClient.addDeviceInterest('general');
    } catch (e) {
      console.error(`[Beams] ${e}`);
    }
  }

  useEffect(() => {
    if (
      accessToken &&
      beamsAuthEndpoint &&
      withBeam &&
      instanceKey &&
      currentUser
    ) {
      initBeams(beamsAuthEndpoint, currentUser.id, instanceKey, accessToken);
    }
  }, [beamsAuthEndpoint, withBeam, accessToken, instanceKey, currentUser]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    if (!clientRef.current) {
      console.log('INIT CONNECTION => ', props);
      clientRef.current = new Pusher(clientKey, {
        cluster: 'eu',
        wsHost: props.pusherHost,
        wssPort: props.pusherPort,
        wsPort: props.pusherPort,
        forceTLS: props.pusherSSL,
        disableStats: true,
        enabledTransports: ['ws', 'wss'],
        channelAuthorization: {
          transport: 'ajax',
          endpoint: props.authEndpoint,
          headersProvider: () => {
            return accessToken
              ? {
                  Authorization: `Bearer ${accessToken}`,
                  'X-Invoke-Lang': language,
                }
              : { 'X-Invoke-Lang': language };
          },
        },
      });

      clientRef.current.connection.bind('connected', function () {
        console.log('Connection State: connected');
        setState(ConnectionState.ONLINE);
      });

      clientRef.current.connection.bind('disconnected', function () {
        console.log('Connection State: disconnected');
        setState(ConnectionState.OFFLINE);
      });

      clientRef.current.connection.bind('unavailable', function () {
        console.log('Connection State: unavailable');
        setState(ConnectionState.OFFLINE);
      });

      clientRef.current.connection.bind('closed', function () {
        console.log('Connection State: closed');
        setState(ConnectionState.OFFLINE);
      });

      clientRef.current.connection.bind('failed', function () {
        console.log('Connection State: failed');
        setState(ConnectionState.OFFLINE);
      });

      clientRef.current.connection.bind('failed', function () {
        console.log('Connection State: failed');
        setState(ConnectionState.OFFLINE);
      });

      clientRef.current.connection.bind('error', function (err: any) {
        console.log(err);
      });

      clientRef.current.connect();
    }
  }, [props, accessToken]);

  const pusherContextValue = useMemo(() => {
    return {
      client: clientRef.current ?? undefined,
      state,
    };
  }, [clientRef.current, state]);

  return accessToken ? (
    <PusherContext.Provider value={pusherContextValue}>
      <ChannelsProvider>{children}</ChannelsProvider>
    </PusherContext.Provider>
  ) : (
    children
  );
};
